import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { Search, X, ChevronDown } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { COMMUNITY_USER_ID } from '../../lib/constants'
import type { Tag, CategoryFilterOption, SubcategoryFilterOption } from '../../types/classification'

interface AdvancedSearchProps {
  isOpen: boolean
  onClose: () => void
  onSearch: (filters: SearchFilters) => void
  onClearFilters: () => void
  currentFilters?: SearchFilters
  mode?: 'discover' | 'curations'
}

export interface SearchFilters {
  query: string
  tags: string[]
  categories: string[] // Category IDs
  subcategories: string[] // Subcategory IDs
  brands: string[]
}

type SearchTarget = 'products' | 'profiles'

type ProfileResult = {
  id: string
  username: string
  first_name: string | null
  last_name: string | null
  avatar_url: string | null
}

const MONO_FONT = "'GT America Mono', 'Courier New', monospace"

// Drops characters that break PostgREST `or` filter syntax.
function profileSearchWords(query: string): string[] {
  return query.trim().replace(/^@/, '').replace(/[,()%*\\'"]/g, '').split(/\s+/).filter(Boolean)
}

export function AdvancedSearch({ isOpen, onClose, onSearch, onClearFilters, currentFilters, mode = 'discover' }: AdvancedSearchProps) {
  const [searchQuery, setSearchQuery] = useState(currentFilters?.query || '')
  const [selectedTags, setSelectedTags] = useState<string[]>(currentFilters?.tags || [])
  const [selectedCategories, setSelectedCategories] = useState<string[]>(currentFilters?.categories || [])
  const [selectedSubcategories, setSelectedSubcategories] = useState<string[]>(currentFilters?.subcategories || [])
  const [selectedBrands, setSelectedBrands] = useState<string[]>(currentFilters?.brands || [])

  const [categories, setCategories] = useState<CategoryFilterOption[]>([])
  const [subcategories, setSubcategories] = useState<SubcategoryFilterOption[]>([])
  const [tags, setTags] = useState<Tag[]>([])
  const [brands, setBrands] = useState<string[]>([])

  const [isLoadingFilters, setIsLoadingFilters] = useState(false)
  const [showBrandDropdown, setShowBrandDropdown] = useState(false)
  const [brandSearch, setBrandSearch] = useState('')
  const loadGeneration = useRef(0)
  const lastLoadedMode = useRef<string | null>(null)

  const navigate = useNavigate()
  const [searchTarget, setSearchTarget] = useState<SearchTarget>('products')
  const [profileQuery, setProfileQuery] = useState('')
  // null while a search is pending
  const [profileResults, setProfileResults] = useState<ProfileResult[] | null>(null)
  const profileSearchGeneration = useRef(0)

  // Load filter data from database, scoped by mode
  // In 'curations' mode, only show filters for items within non-private curations
  useEffect(() => {
    // Skip if already loaded for this mode
    if (lastLoadedMode.current === mode) return

    const currentGeneration = ++loadGeneration.current

    const loadFilterData = async () => {
      try {
        setIsLoadingFilters(true)

        // In curated mode, first get the set of contribution IDs in non-private
        // curations (public + published — a discovery surface, not the feed).
        let contributionScope: string[] | null = null
        if (mode === 'curations') {
          const { data: issueItemsData } = await supabase
            .from('issue_items')
            .select('contribution_id, issues!inner(status)')
            .neq('issues.status', 'private')
          contributionScope = Array.from(new Set(issueItemsData?.map(d => d.contribution_id) || []))
        }

        // Stale check after the scoping query
        if (currentGeneration !== loadGeneration.current) return

        // Build the scoped queries — same queries as before, with optional .in() for curated mode
        let classificationsQuery = supabase
          .from('item_classifications')
          .select('category_id, subcategory_id, contributions!inner(is_active)')
          .eq('contributions.is_active', true)
        if (contributionScope) classificationsQuery = classificationsQuery.in('contribution_id', contributionScope)

        let tagsQuery = supabase
          .from('item_tags')
          .select('tags(id, name), contributions!inner(is_active)')
          .eq('contributions.is_active', true)
        if (contributionScope) tagsQuery = tagsQuery.in('contribution_id', contributionScope)

        let brandsQuery = supabase
          .from('contributions')
          .select('brand')
          .eq('is_active', true)
          .not('brand', 'is', null)
        if (contributionScope) brandsQuery = brandsQuery.in('id', contributionScope)

        const [
          allCategoriesData,
          allSubcategoriesData,
          allClassificationsData,
          allTagsData,
          allBrandsData
        ] = await Promise.all([
          supabase.from('categories').select('id, name').order('name'),
          supabase.from('subcategories').select('id, name, category_id').order('name'),
          classificationsQuery,
          tagsQuery,
          brandsQuery
        ])

        // Build sets of category/subcategory IDs that have items in the DATABASE
        const categoriesWithItems = new Set<string>()
        const subcategoriesWithItems = new Set<string>()

        allClassificationsData.data?.forEach((item) => {
          if (item.category_id) {
            categoriesWithItems.add(item.category_id)
          }
          if (item.subcategory_id) {
            subcategoriesWithItems.add(item.subcategory_id)
          }
        })

        // Map all categories with hasItems flag
        const categoriesWithFlag = (allCategoriesData.data || []).map(cat => ({
          id: cat.id,
          name: cat.name,
          hasItems: categoriesWithItems.has(cat.id)
        }))

        // Map all subcategories with hasItems flag
        const subcategoriesWithFlag = (allSubcategoriesData.data || []).map(sub => ({
          id: sub.id,
          name: sub.name,
          category_id: sub.category_id,
          hasItems: subcategoriesWithItems.has(sub.id)
        }))

        // Extract unique tags from the database data
        const uniqueTags = new Map<string, Tag>()
        allTagsData.data?.forEach((item) => {
          // Supabase returns tags as object for single relation
          const tagData = item.tags as unknown as { id: string; name: string } | null
          if (tagData && tagData.id) {
            uniqueTags.set(tagData.id, tagData)
          }
        })

        // Extract unique brands from the database
        const brandSet = new Set<string>()
        allBrandsData.data?.forEach((item) => {
          if (item.brand) {
            brandSet.add(item.brand)
          }
        })

        // Stale check before setting state
        if (currentGeneration !== loadGeneration.current) return

        // Set the filter data - sort with items first, then alphabetically
        setCategories(categoriesWithFlag.sort((a, b) => {
          if (a.hasItems !== b.hasItems) return a.hasItems ? -1 : 1
          return a.name.localeCompare(b.name)
        }))
        setSubcategories(subcategoriesWithFlag.sort((a, b) => {
          if (a.hasItems !== b.hasItems) return a.hasItems ? -1 : 1
          return a.name.localeCompare(b.name)
        }))
        setTags(Array.from(uniqueTags.values()).sort((a, b) => a.name.localeCompare(b.name)))
        setBrands(Array.from(brandSet).sort())

        lastLoadedMode.current = mode
        setIsLoadingFilters(false)
      } catch (error) {
        if (currentGeneration === loadGeneration.current) {
          console.error('Error loading filter data:', error)
          setIsLoadingFilters(false)
        }
      }
    }

    loadFilterData()
  }, [mode])

  // Sync with current filters when they change
  useEffect(() => {
    if (currentFilters) {
      setSearchQuery(currentFilters.query || '')
      setSelectedTags(currentFilters.tags || [])
      setSelectedCategories(currentFilters.categories || [])
      setSelectedSubcategories(currentFilters.subcategories || [])
      setSelectedBrands(currentFilters.brands || [])
    }
  }, [currentFilters])
  
  // Reset brand search dropdown when closing
  useEffect(() => {
    if (!isOpen) {
      setBrandSearch('')
      setShowBrandDropdown(false)
      setProfileQuery('')
    }
  }, [isOpen])

  useEffect(() => {
    const generation = ++profileSearchGeneration.current
    setProfileResults(null)
    const words = profileSearchWords(profileQuery)
    if (searchTarget !== 'profiles' || words.join('').length < 2) return

    const timer = setTimeout(async () => {
      let query = supabase
        .from('users')
        .select('id, username, first_name, last_name, avatar_url')
        .not('username', 'is', null)
        .eq('is_suspended', false)
        .neq('id', COMMUNITY_USER_ID)
      for (const w of words) {
        query = query.or(`first_name.ilike.%${w}%,last_name.ilike.%${w}%,username.ilike.%${w}%`)
      }
      const { data, error } = await query.order('username').limit(20)
      if (generation !== profileSearchGeneration.current) return
      if (error) console.error('Error searching profiles:', error)
      setProfileResults((data as ProfileResult[] | null) || [])
    }, 250)
    return () => clearTimeout(timer)
  }, [profileQuery, searchTarget])

  const openProfile = (profile: ProfileResult) => {
    navigate(`/profile/${profile.username}`)
    onClose()
  }

  const toggleTag = (tag: string) => {
    setSelectedTags(prev =>
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    )
  }

  const toggleCategory = (categoryId: string) => {
    setSelectedCategories(prev => {
      const newSelection = prev.includes(categoryId)
        ? prev.filter(c => c !== categoryId)
        : [...prev, categoryId]

      // If deselecting a category, also deselect its subcategories
      if (!newSelection.includes(categoryId)) {
        const categorySubcategoryIds = subcategories
          .filter(s => s.category_id === categoryId)
          .map(s => s.id)
        setSelectedSubcategories(prev =>
          prev.filter(s => !categorySubcategoryIds.includes(s))
        )
      }

      return newSelection
    })
  }

  const toggleSubcategory = (subcategoryId: string) => {
    setSelectedSubcategories(prev =>
      prev.includes(subcategoryId)
        ? prev.filter(s => s !== subcategoryId)
        : [...prev, subcategoryId]
    )
  }

  const toggleBrand = (brand: string) => {
    setSelectedBrands(prev =>
      prev.includes(brand)
        ? prev.filter(b => b !== brand)
        : [...prev, brand]
    )
  }

  // Get filtered subcategories based on selected categories
  const filteredSubcategories = selectedCategories.length > 0
    ? subcategories.filter(sub => selectedCategories.includes(sub.category_id))
    : []

  const handleSearch = () => {
    const filters: SearchFilters = {
      query: searchQuery,
      tags: selectedTags,
      categories: selectedCategories,
      subcategories: selectedSubcategories,
      brands: selectedBrands
    }
    onSearch(filters)
    // Close the search panel with animation
    setTimeout(onClose, 100)
  }

  const hasActiveFilters = searchQuery || selectedTags.length > 0 || selectedCategories.length > 0 || selectedSubcategories.length > 0 || selectedBrands.length > 0

  const isProfiles = searchTarget === 'profiles'
  const hasProfileQuery = profileSearchWords(profileQuery).join('').length >= 2
  const inputValue = isProfiles ? profileQuery : searchQuery
  const setInputValue = isProfiles ? setProfileQuery : setSearchQuery

  const filteredBrands = brands.filter(brand => 
    brand.toLowerCase().includes(brandSearch.toLowerCase())
  )

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/20 z-30"
            style={{ top: '80px' }}
            onClick={onClose}
          />

          {/* Search Panel */}
          <motion.div
            initial={{ y: '-100%' }}
            animate={{ y: 0 }}
            exit={{ y: '-100%' }}
            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
            className="fixed left-0 right-0 bg-white border-b shadow-2xl z-40"
            style={{
              top: '80px', // Below the header (h-20 = 80px)
              maxHeight: 'calc(100vh - 80px)',
              overflowY: 'auto'
            }}
          >
            <div className="container mx-auto px-4 py-6 relative">
            {/* Products / Profiles toggle */}
            <div className="flex justify-center gap-2 mb-4">
              {(['products', 'profiles'] as const).map(target => (
                <button
                  key={target}
                  onClick={() => setSearchTarget(target)}
                  className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                    searchTarget === target
                      ? 'bg-black text-white'
                      : 'bg-white text-black border border-gray-300 hover:border-gray-400'
                  }`}
                  style={{ fontFamily: MONO_FONT }}
                >
                  {target === 'products' ? 'Products' : 'Profiles'}
                </button>
              ))}
            </div>

            {/* Search Input */}
            <div className="mb-6">
              <div className="relative max-w-2xl mx-auto">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder={isProfiles ? 'Search by name or @handle...' : 'Search by keyword...'}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter') return
                    if (!isProfiles) handleSearch()
                    else if (hasProfileQuery && profileResults?.[0]) openProfile(profileResults[0])
                  }}
                  className="w-full pl-10 pr-10 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:bg-white focus:border-gray-300 transition-all"
                />
                {inputValue && (
                  <button
                    onClick={() => setInputValue('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded"
                  >
                    <X className="h-4 w-4 text-gray-500" />
                  </button>
                )}
              </div>
            </div>

            {isProfiles ? (
            <>
            {/* Profile Results */}
            {hasProfileQuery && (
              <div className="max-w-2xl mx-auto mb-6">
                {profileResults === null ? (
                  <div className="py-4 text-center text-sm text-gray-500" style={{ fontFamily: MONO_FONT }}>
                    Searching...
                  </div>
                ) : profileResults.length === 0 ? (
                  <div className="py-4 text-center text-sm text-gray-500" style={{ fontFamily: MONO_FONT }}>
                    No profiles found
                  </div>
                ) : (
                  <div className="divide-y">
                    {profileResults.map(profile => {
                      const fullName = `${profile.first_name || ''} ${profile.last_name || ''}`.trim()
                      return (
                        <button
                          key={profile.id}
                          onClick={() => openProfile(profile)}
                          className="w-full py-3 px-2 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left rounded"
                        >
                          {profile.avatar_url ? (
                            <img
                              src={profile.avatar_url}
                              alt={fullName || profile.username}
                              className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0 text-gray-600 font-medium">
                              {(fullName || profile.username).charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 truncate">{fullName || profile.username}</p>
                            <p className="text-sm text-gray-500 truncate">@{profile.username}</p>
                          </div>
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end pt-4 border-t">
              <button
                onClick={onClose}
                className="px-6 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                style={{ fontFamily: MONO_FONT }}
              >
                Cancel
              </button>
            </div>
            </>
            ) : (
            <>
            {/* Categories Section */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-3" style={{ fontFamily: "'GT America Mono', 'Courier New', monospace" }}>
                CATEGORY
              </h3>
              <div className="flex flex-wrap gap-2">
                {categories.map(category => (
                  <button
                    key={category.id}
                    onClick={() => category.hasItems && toggleCategory(category.id)}
                    disabled={!category.hasItems}
                    title={!category.hasItems ? 'No items in this category' : undefined}
                    className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                      selectedCategories.includes(category.id)
                        ? 'bg-black text-white'
                        : category.hasItems
                          ? 'bg-white text-black border border-gray-300 hover:border-gray-400 cursor-pointer'
                          : 'bg-gray-50 text-gray-400 border border-gray-200 cursor-not-allowed opacity-60'
                    }`}
                    style={{ fontFamily: "'GT America Mono', 'Courier New', monospace" }}
                  >
                    {category.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Subcategories Section - only show if category is selected */}
            {filteredSubcategories.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-700 mb-3" style={{ fontFamily: "'GT America Mono', 'Courier New', monospace" }}>
                  SUBCATEGORY
                </h3>
                <div className="flex flex-wrap gap-2">
                  {filteredSubcategories.map(subcategory => (
                    <button
                      key={subcategory.id}
                      onClick={() => subcategory.hasItems && toggleSubcategory(subcategory.id)}
                      disabled={!subcategory.hasItems}
                      title={!subcategory.hasItems ? 'No items in this subcategory' : undefined}
                      className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                        selectedSubcategories.includes(subcategory.id)
                          ? 'bg-black text-white'
                          : subcategory.hasItems
                            ? 'bg-white text-black border border-gray-300 hover:border-gray-400 cursor-pointer'
                            : 'bg-gray-50 text-gray-400 border border-gray-200 cursor-not-allowed opacity-60'
                      }`}
                      style={{ fontFamily: "'GT America Mono', 'Courier New', monospace" }}
                    >
                      {subcategory.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Tags Section */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-3" style={{ fontFamily: "'GT America Mono', 'Courier New', monospace" }}>
                TAGS
              </h3>
              <div className="flex flex-wrap gap-2">
                {tags.map(tag => (
                  <button
                    key={tag.id}
                    onClick={() => toggleTag(tag.name)}
                    className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                      selectedTags.includes(tag.name)
                        ? 'bg-black text-white'
                        : 'bg-white text-black border border-gray-300 hover:border-gray-400'
                    }`}
                    style={{ fontFamily: "'GT America Mono', 'Courier New', monospace" }}
                  >
                    {tag.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Brands Section */}
            <div className="mb-6" style={{ position: 'relative' }}>
              <h3 className="text-sm font-medium text-gray-700 mb-3" style={{ fontFamily: "'GT America Mono', 'Courier New', monospace" }}>
                BRANDS
              </h3>
              <div className="flex flex-wrap items-center gap-2">
                {/* Selected brands as bubbles */}
                {selectedBrands.map(brand => (
                  <button
                    key={brand}
                    onClick={() => toggleBrand(brand)}
                    className="px-3 py-1.5 rounded-full text-sm bg-black text-white flex items-center gap-2"
                    style={{ fontFamily: "'GT America Mono', 'Courier New', monospace" }}
                  >
                    {brand}
                    <X className="h-3 w-3" />
                  </button>
                ))}
                
                {/* Dropdown button */}
                <div className="relative">
                  <button
                    onClick={() => setShowBrandDropdown(!showBrandDropdown)}
                    className="px-4 py-1.5 bg-white border border-gray-300 rounded-full text-sm flex items-center gap-2 hover:border-gray-400 transition-colors"
                    style={{ fontFamily: "'GT America Mono', 'Courier New', monospace" }}
                  >
                    <span>Add brand</span>
                    <ChevronDown className={`h-3 w-3 text-gray-500 transition-transform ${
                      showBrandDropdown ? 'rotate-180' : ''
                    }`} />
                  </button>
                  
                  {showBrandDropdown && (
                    <div
                      className="absolute bottom-full left-0 mb-1 bg-white border border-gray-200 rounded-lg shadow-xl"
                      style={{
                        zIndex: 9999,
                        minWidth: '200px',
                        maxHeight: '256px',
                        overflowY: 'auto',
                        overflowX: 'hidden'
                      }}
                    >
                      <div className="p-2 border-b">
                        <input
                          type="text"
                          placeholder="Search brands..."
                          value={brandSearch}
                          onChange={(e) => setBrandSearch(e.target.value)}
                          className="w-full px-3 py-1.5 bg-gray-50 border border-gray-200 rounded text-sm focus:outline-none focus:border-gray-300"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      <div>
                        {selectedBrands.length > 0 && (
                          <button
                            onClick={() => {
                              setSelectedBrands([])
                              setBrandSearch('')
                            }}
                            className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 text-gray-500"
                            style={{ fontFamily: "'GT America Mono', 'Courier New', monospace" }}
                          >
                            Clear all brands
                          </button>
                        )}
                        {isLoadingFilters ? (
                          <div className="px-3 py-4 text-center text-sm text-gray-500">
                            Loading...
                          </div>
                        ) : filteredBrands.length === 0 ? (
                          <div className="px-3 py-4 text-center text-sm text-gray-500">
                            No brands found
                          </div>
                        ) : (
                          filteredBrands
                            .filter(brand => !selectedBrands.includes(brand))
                            .map(brand => (
                              <button
                                key={brand}
                                onClick={() => {
                                  toggleBrand(brand)
                                  setBrandSearch('')
                                }}
                                className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50"
                                style={{ fontFamily: "'GT America Mono', 'Courier New', monospace" }}
                              >
                                {brand}
                              </button>
                            ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Search Actions */}
            <div className="flex items-center justify-between pt-4 border-t">
              <div>
                {hasActiveFilters && (
                  <button
                    onClick={() => {
                      setSearchQuery('')
                      setSelectedTags([])
                      setSelectedCategories([])
                      setSelectedSubcategories([])
                      setSelectedBrands([])
                      // Clear filters to show main feed
                      onClearFilters()
                    }}
                    className="text-gray-500 hover:text-gray-700 underline transition-colors"
                    style={{ fontFamily: "'GT America Mono', 'Courier New', monospace" }}
                  >
                    Clear all filters
                  </button>
                )}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className="px-6 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                  style={{ fontFamily: "'GT America Mono', 'Courier New', monospace" }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSearch}
                  className="px-8 py-2 bg-black text-white rounded-full hover:bg-gray-800 transition-colors flex items-center gap-2"
                  style={{ fontFamily: "'GT America Mono', 'Courier New', monospace" }}
                >
                  <Search className="h-4 w-4" />
                  Search
                </button>
              </div>
            </div>
            </>
            )}
          </div>
        </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}